import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { useEffect } from "react";

import Header from "./components/Header";
import Navbar from "./components/Navbar";

import Home from "./Pages/Home.js";
import Login from "./Login";
import Dashboard from "./Dashboard";
import Students from "./Students";
import About from "./components/About";
import Courses from "./components/Courses";
import Contact from "./components/Contact";
import OurStory from "./Pages/OurStory";
import StudentLogin from "./Pages/StudentLogin";
import OurCourses from "./Pages/OurCourses";
import OurCentre from "./Pages/OurCentre";
import StudentRegistration from "./Pages/StudentRegistration";
import StudentVerification from "./Pages/StudentVerification";
import ResultVerification from "./Pages/ResultVerification";
import Download from "./Pages/Download";
import Gallery from "./Pages/Gallery";
import FranchiseApply from "./Pages/FranchiseApply";
import FranchiseApplicationStatus from "./Pages/FranchiseApplicationStatus";
import { policyPages, PolicyRoutePage } from "./Pages/PolicyPages";
import ProtectedRoute from "./ProtectedRoute";
import AddCategory from "./Dashboard/AddCategory";
import ViewCategory from "./Dashboard/ViewCategory";
import CourseModuleManager from "./Dashboard/CourseModuleManager";
import CourseCategoryManager from "./Dashboard/CourseCategoryManager";
import OurCoursesManager from "./Dashboard/OurCoursesManager";
import CourseFeesManager from "./Dashboard/CourseFeesManager";
import HomeSetting from "./Dashboard/HomeSetting";
import AboutSetting from "./Dashboard/AboutSetting";
import State from "./Dashboard/State";
import District from "./Dashboard/District";
import SessionControl from "./Dashboard/SessionControl";
import FranchiseApplication from "./Dashboard/FranchiseApplication";
import NewFranchise from "./Dashboard/NewFranchise";
import ViewFranchise, { BlacklistedFranchise, HoldRejectFranchise, TotalFranchise } from "./Dashboard/ViewFranchise";
import ViewStudentApplications from "./Dashboard/ViewStudentApplications";
import ViewStudents from "./Dashboard/ViewStudents";
import OurStudentsAdmin from "./Dashboard/OurStudentsAdmin";
import AdminStatementPage from "./Dashboard/AdminStatementPage";
import LoadMoneyRequest from "./Dashboard/LoadMoneyRequest";
import ViewMoneyRequest from "./Dashboard/ViewMoneyRequest";
import RejectTransaction from "./Dashboard/RejectTransaction";
import PaymentAccountSetting from "./Dashboard/PaymentAccountSetting";
import { AdminPostalOrderPage, AdminPostalDispatchPage } from "./Dashboard/FrontOfficePages";
import { AdminFeaturePlaceholder, adminFeatureRoutes } from "./Dashboard/AdminFeaturePages";
import FranchiseLogin from "./Pages/FranchiseLogin";
import FranchiseAutoLogin from "./Pages/FranchiseAutoLogin";
import FranchiseDashboard from "./Pages/FranchiseDashboard";
import FranchiseProtectedRoute from "./FranchiseProtectedRoute";
import StudentProtectedRoute from "./StudentProtectedRoute";
import StudentDashboard from "./Pages/StudentDashboard";
import {
  Overview,
  CourseView,
  CourseFees,
  NewApplication,
  PostalOrder,
  PostalDispatch,
  MyWallet,
  LoadMoney,
  ViewStatement,
  ExamList,
  ExamQuestions,
  OnlineExamResult,
  MarksheetDownload,
  CertificateDownload,
  CertifiedStudents,
  FranchiseTraining,
  ProfileSetting,
  NewMessage,
  ViewMessage,
  MessageRequest,
  TicketBox,
} from "./Pages/FranchiseModules/PortalPages";
import {
  AppliedStudents,
  AcceptedStudents,
  RegisteredStudents,
  ExamCompletedStudents as FranchiseExamCompletedStudents,
} from "./Pages/FranchiseModules/StudentWorkflowPages";
import OurStudents from "./Pages/FranchiseModules/OurStudents";
import ExamCompletedStudents from "./Dashboard/ExamCompletedStudents";
import { AdminMarksheetDownloads, AdminCertificateDownloads } from "./Dashboard/CertificationDownloadsAdmin";
import AMCIDesignPage from "./Dashboard/AMCIDesignPage";
import GradePointSettingPage from "./Dashboard/GradePointSettingPage";
import { CertifiedStudentsAdmin, FranchiseTrainingAdmin, TicketSystemAdmin } from "./Dashboard/NewFeaturePages";
import GalleryManager from "./Dashboard/GalleryManager";
import TopbarFooterSetting from "./Dashboard/TopbarFooterSetting";
import DownloadManager from "./Dashboard/DownloadManager";
import AdminUserController from "./Dashboard/AdminUserController";
import AdminChatPage from "./Dashboard/AdminChatPage";
import AdminFeedbackBox from "./Dashboard/AdminFeedbackBox";
import NotificationCenterPage from "./Dashboard/NotificationCenterPage";
import NotificationSettingsPage from "./Dashboard/NotificationSettingsPage";
import ContactInboxPage from "./Dashboard/ContactInboxPage";
import PolicySettingPage from "./Dashboard/PolicySettingPage";
import { policySettingItems } from "./constants/policySettings";
import NewExamPage from "./Dashboard/NewExamPage";
import ViewExamPage from "./Dashboard/ViewExamPage";
import NewQuestionModulePage from "./Dashboard/NewQuestionModulePage";
import ViewQuestionModulePage from "./Dashboard/ViewQuestionModulePage";
import CsvUploadPage from "./Dashboard/CsvUploadPage";
import OnlineExamResultPage from "./Dashboard/OnlineExamResultPage";

const publicPolicyPages = policyPages.filter(
  (page) => !["/our-policy/territorial-policy", "/our-policy/anti-ragging-student-conduct"].includes(page.path)
);

function AppContent() {
  const location = useLocation();
  const siteTitle = "NIDCS (National Institute of Digital careers and Skills)";
  const faviconHref = `${process.env.PUBLIC_URL || ""}/favicon.png?v=20260423a`;

  useEffect(() => {
    document.title = siteTitle;

    const ensureIcon = (rel) => {
      let link = document.querySelector(`link[rel='${rel}']`);
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", rel);
        document.head.appendChild(link);
      }
      link.setAttribute("href", faviconHref);
      if (rel !== "apple-touch-icon") {
        link.setAttribute("type", "image/png");
      }
    };

    ensureIcon("icon");
    ensureIcon("shortcut icon");
    ensureIcon("apple-touch-icon");
  }, [location.pathname, faviconHref, siteTitle]);

  useEffect(() => {
    const applyLazyToImages = () => {
      const images = document.querySelectorAll("img");
      images.forEach((img) => {
        if (img.dataset.eagerMedia === "true") return;
        if (!img.getAttribute("loading")) {
          img.setAttribute("loading", "lazy");
        }
        if (!img.getAttribute("decoding")) {
          img.setAttribute("decoding", "async");
        }
      });
    };

    applyLazyToImages();
    const timer = window.setTimeout(applyLazyToImages, 0);
    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  const isAdminPage =
    location.pathname.startsWith("/administrator") ||
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/students") ||
    location.pathname.startsWith("/franchise-login") ||
    location.pathname.startsWith("/franchise-dashboard") ||
    location.pathname.startsWith("/student-dashboard");

  return (
    <>
      {!isAdminPage && <Header />}
      {!isAdminPage && <Navbar />}

      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/administrator" element={<Login />} />
        <Route path="/administrator/" element={<Login />} />
        <Route path="/about" element={<About />} />
        <Route path="/courses" element={<Courses />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/our-story" element={<OurStory />} />
        <Route path="/student-login" element={<StudentLogin />} />
        <Route path="/our-courses" element={<OurCourses />} />
        <Route path="/our-centre" element={<OurCentre />} />
        <Route path="/student-registration" element={<StudentRegistration />} />
        <Route path="/student-verification" element={<StudentVerification />} />
        <Route path="/result-verification" element={<ResultVerification />} />
        <Route path="/downloadcenter" element={<Download />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/franchise-apply" element={<FranchiseApply />} />
        <Route path="/franchise-application-status" element={<FranchiseApplicationStatus />} />
        <Route path="/franchise-login" element={<FranchiseLogin />} />
        <Route path="/franchise-auto-login" element={<FranchiseAutoLogin />} />
        <Route path="/student-dashboard" element={<StudentProtectedRoute><StudentDashboard /></StudentProtectedRoute>} />
        {publicPolicyPages.map((page) => (
          <Route key={page.path} path={page.path} element={<PolicyRoutePage page={page} />} />
        ))}

        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/students" element={<ProtectedRoute><Students /></ProtectedRoute>} />
        <Route path="/dashboard/course-modules" element={<ProtectedRoute><CourseModuleManager /></ProtectedRoute>} />
        <Route path="/dashboard/course-categories" element={<ProtectedRoute><CourseCategoryManager /></ProtectedRoute>} />
        <Route path="/dashboard/our-courses" element={<ProtectedRoute><OurCoursesManager /></ProtectedRoute>} />
        <Route path="/dashboard/course-fees" element={<ProtectedRoute><CourseFeesManager /></ProtectedRoute>} />
        <Route path="/dashboard/new-module" element={<ProtectedRoute><CourseModuleManager /></ProtectedRoute>} />
        <Route path="/dashboard/view-module" element={<ProtectedRoute><CourseModuleManager /></ProtectedRoute>} />
        <Route path="/dashboard/add-category" element={<ProtectedRoute><AddCategory /></ProtectedRoute>} />
        <Route path="/dashboard/view-category" element={<ProtectedRoute><ViewCategory /></ProtectedRoute>} />
        <Route path="/dashboard/new-course" element={<ProtectedRoute><OurCoursesManager /></ProtectedRoute>} />
        <Route path="/dashboard/view-course" element={<ProtectedRoute><OurCoursesManager /></ProtectedRoute>} />
        <Route path="/dashboard/new-course-fees" element={<ProtectedRoute><CourseFeesManager /></ProtectedRoute>} />
        <Route path="/dashboard/view-course-fees" element={<ProtectedRoute><CourseFeesManager /></ProtectedRoute>} />
        <Route path="/dashboard/home-setting" element={<ProtectedRoute><HomeSetting /></ProtectedRoute>} />
        <Route path="/dashboard/about-setting" element={<ProtectedRoute><AboutSetting /></ProtectedRoute>} />
        <Route path="/dashboard/gallery" element={<ProtectedRoute><GalleryManager /></ProtectedRoute>} />
        <Route path="/dashboard/download-setting" element={<ProtectedRoute><DownloadManager /></ProtectedRoute>} />
        <Route path="/dashboard/topbar-footer-setting" element={<ProtectedRoute><TopbarFooterSetting /></ProtectedRoute>} />
        <Route path="/dashboard/admin-users" element={<ProtectedRoute><AdminUserController /></ProtectedRoute>} />
        <Route path="/dashboard/session-control" element={<ProtectedRoute><SessionControl /></ProtectedRoute>} />
        <Route path="/dashboard/state" element={<ProtectedRoute><State /></ProtectedRoute>} />
        <Route path="/dashboard/district" element={<ProtectedRoute><District /></ProtectedRoute>} />
        <Route path="/dashboard/franchise-application" element={<ProtectedRoute><FranchiseApplication /></ProtectedRoute>} />
        <Route path="/dashboard/new-franchise" element={<ProtectedRoute><NewFranchise /></ProtectedRoute>} />
        <Route path="/dashboard/view-franchise" element={<ProtectedRoute><ViewFranchise /></ProtectedRoute>} />
        <Route path="/dashboard/hold-reject-franchise" element={<ProtectedRoute><HoldRejectFranchise /></ProtectedRoute>} />
        <Route path="/dashboard/blacklisted-franchise" element={<ProtectedRoute><BlacklistedFranchise /></ProtectedRoute>} />
        <Route path="/dashboard/total-franchise" element={<ProtectedRoute><TotalFranchise /></ProtectedRoute>} />
        <Route path="/dashboard/view-application" element={<ProtectedRoute><ViewStudentApplications /></ProtectedRoute>} />
        <Route path="/dashboard/view-student" element={<ProtectedRoute><ViewStudents /></ProtectedRoute>} />
        <Route path="/dashboard/exam-complete" element={<ProtectedRoute><ExamCompletedStudents /></ProtectedRoute>} />
        <Route path="/dashboard/our-student" element={<ProtectedRoute><OurStudentsAdmin /></ProtectedRoute>} />
        <Route path="/dashboard/certified-student" element={<ProtectedRoute><CertifiedStudentsAdmin /></ProtectedRoute>} />
        <Route path="/dashboard/statement" element={<ProtectedRoute><AdminStatementPage /></ProtectedRoute>} />
        <Route path="/dashboard/postal-order" element={<ProtectedRoute><AdminPostalOrderPage /></ProtectedRoute>} />
        <Route path="/dashboard/postal-dispatch" element={<ProtectedRoute><AdminPostalDispatchPage /></ProtectedRoute>} />
        <Route path="/dashboard/deposit" element={<ProtectedRoute><LoadMoneyRequest /></ProtectedRoute>} />
        <Route path="/dashboard/view-deposit" element={<ProtectedRoute><ViewMoneyRequest /></ProtectedRoute>} />
        <Route path="/dashboard/reject-transaction" element={<ProtectedRoute><RejectTransaction /></ProtectedRoute>} />
        <Route path="/dashboard/payment-account-setting" element={<ProtectedRoute><PaymentAccountSetting /></ProtectedRoute>} />
        <Route path="/dashboard/amci-design" element={<ProtectedRoute><AMCIDesignPage /></ProtectedRoute>} />
        <Route path="/dashboard/franchise-affiliated-cert-design" element={<ProtectedRoute><AMCIDesignPage /></ProtectedRoute>} />
        <Route path="/dashboard/new-grade" element={<ProtectedRoute><GradePointSettingPage /></ProtectedRoute>} />
        <Route path="/dashboard/franchise-training" element={<ProtectedRoute><FranchiseTrainingAdmin /></ProtectedRoute>} />
        <Route path="/dashboard/ticket-system" element={<ProtectedRoute><TicketSystemAdmin /></ProtectedRoute>} />
        <Route path="/dashboard/new-message" element={<ProtectedRoute><AdminChatPage /></ProtectedRoute>} />
        <Route path="/dashboard/contact" element={<ProtectedRoute><ContactInboxPage /></ProtectedRoute>} />
        <Route path="/dashboard/notification-panel" element={<ProtectedRoute><NotificationCenterPage /></ProtectedRoute>} />
        <Route path="/dashboard/feedback-box" element={<ProtectedRoute><AdminFeedbackBox /></ProtectedRoute>} />
        <Route path="/dashboard/notification-settings" element={<ProtectedRoute><NotificationSettingsPage /></ProtectedRoute>} />
        <Route path="/dashboard/new-exam" element={<ProtectedRoute><NewExamPage /></ProtectedRoute>} />
        <Route path="/dashboard/view-exam" element={<ProtectedRoute><ViewExamPage /></ProtectedRoute>} />
        <Route path="/dashboard/new-mcq" element={<ProtectedRoute><NewQuestionModulePage /></ProtectedRoute>} />
        <Route path="/dashboard/view-mcq" element={<ProtectedRoute><ViewQuestionModulePage /></ProtectedRoute>} />
        <Route path="/dashboard/csv-upload" element={<ProtectedRoute><CsvUploadPage /></ProtectedRoute>} />
        <Route path="/dashboard/exam-result" element={<ProtectedRoute><OnlineExamResultPage /></ProtectedRoute>} />
        {policySettingItems.map((item) => (
          <Route
            key={`policy-setting-${item.key}`}
            path={`/dashboard/policy-setting/${item.key}`}
            element={<ProtectedRoute><PolicySettingPage /></ProtectedRoute>}
          />
        ))}
        {adminFeatureRoutes.filter((page) => !["/dashboard/deposit", "/dashboard/view-deposit", "/dashboard/reject-transaction", "/dashboard/payment-account-setting", "/dashboard/marksheet", "/dashboard/certificate", "/dashboard/statement", "/dashboard/new-grade", "/dashboard/amci-design", "/dashboard/franchise-affiliated-cert-design", "/dashboard/gallery", "/dashboard/topbar-footer-setting", "/dashboard/download-setting", "/dashboard/new-message", "/dashboard/feedback-box", "/dashboard/contact", "/dashboard/new-exam", "/dashboard/view-exam", "/dashboard/new-mcq", "/dashboard/view-mcq", "/dashboard/csv-upload", "/dashboard/exam-result"].includes(page.path)).map((page) => (
          <Route
            key={page.path}
            path={page.path}
            element={
              <ProtectedRoute>
                <AdminFeaturePlaceholder {...page} />
              </ProtectedRoute>
            }
          />
        ))}

        <Route path="/franchise-dashboard" element={<FranchiseProtectedRoute><FranchiseDashboard /></FranchiseProtectedRoute>}>
          <Route index element={<Overview />} />
          <Route path="course/view" element={<CourseView />} />
          <Route path="course/fees" element={<CourseFees />} />
          <Route path="student/new-application" element={<NewApplication />} />
          <Route path="student/applied-student" element={<AppliedStudents />} />
          <Route path="student/accepted-student" element={<AcceptedStudents />} />
          <Route path="student/registered-student" element={<RegisteredStudents />} />
          <Route path="student/exam-completed-student" element={<FranchiseExamCompletedStudents />} />
          <Route path="student/our-student" element={<OurStudents />} />
          <Route path="student/certified-student" element={<CertifiedStudents />} />
          <Route path="student/view-application" element={<AppliedStudents />} />
          <Route path="training/videos" element={<FranchiseTraining />} />
          <Route path="front-office/postal-order" element={<PostalOrder />} />
          <Route path="front-office/postal-dispatch" element={<PostalDispatch />} />
          <Route path="payment/wallet" element={<MyWallet />} />
          <Route path="payment/load-money" element={<LoadMoney />} />
          <Route path="account/statement" element={<ViewStatement />} />
          <Route path="exam/list" element={<ExamList />} />
          <Route path="exam/questions" element={<ExamQuestions />} />
          <Route path="exam/results" element={<OnlineExamResult />} />
          <Route path="certification/marksheet" element={<MarksheetDownload />} />
          <Route path="certification/certificate" element={<CertificateDownload />} />
          <Route path="settings/profile" element={<ProfileSetting />} />
          <Route path="message/new" element={<NewMessage />} />
          <Route path="message/view" element={<ViewMessage />} />
          <Route path="message/request" element={<MessageRequest />} />
          <Route path="message/ticket-box" element={<TicketBox />} />
        </Route>
        <Route path="/dashboard/marksheet" element={<ProtectedRoute><AdminMarksheetDownloads /></ProtectedRoute>} />
        <Route path="/dashboard/certificate" element={<ProtectedRoute><AdminCertificateDownloads /></ProtectedRoute>} />
      </Routes>
    </>
  );
}

function App() {
  const baseName = window.location.pathname.includes("/finaloutput/public_html")
    ? "/finaloutput/public_html"
    : "/";

  return (
    <Router basename={baseName}>
      <AppContent />
    </Router>
  );
}

export default App;
